import React, { useState, useEffect } from "react";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";

const SystemSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState("general");
    const [generalSettings, setGeneralSettings] = useState({
        siteTitle: "Carib-Bargains",
        tagline: "Your Caribbean Marketplace",
        contactEmail: "support@caribbargains.com",
        contactPhone: "+1 (868) 000-0000",
        maintenanceMode: false,
        logo: null,
    });
    const [smtpSettings, setSmtpSettings] = useState({
        host: "",
        port: 587,
        username: "",
        password: "",
        encryption: "TLS",
        fromEmail: "",
        fromName: "Carib-Bargains",
        debug: false,
    });
    const [securitySettings, setSecuritySettings] = useState({
        sslRedirect: true,
        sessionTimeout: 60,
        maxLoginAttempts: 5,
    });
    const [testRecipient, setTestRecipient] = useState("");

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await AdminService.getSystemSettings();
            if (response.data.data) {
                const data = response.data.data;
                if (data.general) {
                    setGeneralSettings(prev => ({ ...prev, ...data.general }));
                    setSecuritySettings({
                        sslRedirect: data.general.sslRedirect ?? true,
                        sessionTimeout: data.general.sessionTimeout ?? 60,
                        maxLoginAttempts: data.general.maxLoginAttempts ?? 5
                    });
                }
                if (data.smtp) setSmtpSettings(prev => ({ ...prev, ...data.smtp }));
            }
        } catch {
            console.log("Settings API not available, using defaults");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGeneral = async () => {
        setSaving(true);
        try {
            const response = await AdminService.updateGeneralSettings(generalSettings);
            if (response.data.status) {
                toast.success("General settings saved successfully");
            } else {
                toast.error(response.data.message || "Failed to save settings");
            }
        } catch (error) {
            console.error("Error saving general settings:", error);
            toast.error("An error occurred while saving settings");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSmtp = async () => {
        setSaving(true);
        try {
            const payload = { ...smtpSettings, port: parseInt(smtpSettings.port) };
            await AdminService.updateSmtpSettings(payload);
            toast.success("SMTP settings updated successfully");
        } catch (error) {
            console.error("Error saving SMTP settings:", error);
            toast.error("Failed to update SMTP settings");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSecurity = async () => {
        setSaving(true);
        try {
            await AdminService.updateSecuritySettings(securitySettings);
            toast.success("Security settings updated successfully");
        } catch (error) {
            console.error("Error saving security settings:", error);
            toast.error("Failed to update security settings");
        } finally {
            setSaving(false);
        }
    };

    const handleTestSmtp = async () => {
        if (!testRecipient.trim()) {
            toast.error("Enter a test email address");
            return;
        }
        try {
            const response = await AdminService.testSmtp({ ...smtpSettings, testEmail: testRecipient });
            if (response.data.status) {
                toast.success(response.data.message);
            } else {
                toast.error(response.data.message || "SMTP test failed");
            }
        } catch (error) {
            console.error("Error testing SMTP:", error);
            toast.error("An error occurred during SMTP test");
        }
    };

    if (loading) {
        return (
            <div className="admin-empty-state">
                <i className="fa fa-spinner fa-spin"></i>
                <p>Loading settings...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="admin-tabs">
                <button className={`admin-tab ${activeSection === "general" ? "active" : ""}`} onClick={() => setActiveSection("general")}>
                    General
                </button>
                <button className={`admin-tab ${activeSection === "smtp" ? "active" : ""}`} onClick={() => setActiveSection("smtp")}>
                    Email / SMTP
                </button>
                <button className={`admin-tab ${activeSection === "security" ? "active" : ""}`} onClick={() => setActiveSection("security")}>
                    Security
                </button>
            </div>

            {activeSection === "general" && (
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3>General Settings</h3>
                    </div>
                    <div className="admin-card-body">
                        <div className="admin-settings-section">
                            <h4>Site Information</h4>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Site Title</label>
                                    <input
                                        type="text"
                                        className="admin-form-control"
                                        value={generalSettings.siteTitle}
                                        onChange={(e) => setGeneralSettings({ ...generalSettings, siteTitle: e.target.value })}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Tagline</label>
                                    <input
                                        type="text"
                                        className="admin-form-control"
                                        value={generalSettings.tagline}
                                        onChange={(e) => setGeneralSettings({ ...generalSettings, tagline: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Contact Email</label>
                                    <input
                                        type="email"
                                        className="admin-form-control"
                                        value={generalSettings.contactEmail}
                                        onChange={(e) => setGeneralSettings({ ...generalSettings, contactEmail: e.target.value })}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Contact Phone</label>
                                    <input
                                        type="text"
                                        className="admin-form-control"
                                        value={generalSettings.contactPhone}
                                        onChange={(e) => setGeneralSettings({ ...generalSettings, contactPhone: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="admin-form-group">
                                <label>Site Logo</label>
                                <input
                                    type="file"
                                    className="admin-form-control"
                                    accept="image/*"
                                    onChange={(e) => setGeneralSettings({ ...generalSettings, logo: e.target.files[0] })}
                                />
                            </div>
                        </div>

                        <div className="admin-settings-section">
                            <h4>Maintenance Mode</h4>
                            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                <label className="admin-toggle">
                                    <input
                                        type="checkbox"
                                        checked={generalSettings.maintenanceMode}
                                        onChange={(e) => setGeneralSettings({ ...generalSettings, maintenanceMode: e.target.checked })}
                                    />
                                    <span className="admin-toggle-slider"></span>
                                </label>
                                <span style={{ fontSize: "14px", color: generalSettings.maintenanceMode ? "#dc3545" : "#6c757d" }}>
                                    {generalSettings.maintenanceMode ? "Maintenance mode is ON — site is offline for users" : "Maintenance mode is OFF — site is live"}
                                </span>
                            </div>
                        </div>

                        <div className="admin-form-actions">
                            <button className="admin-btn admin-btn-primary" onClick={handleSaveGeneral}>
                                <i className="fa fa-save"></i> Save Settings
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeSection === "smtp" && (
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3>SMTP / Email Configuration</h3>
                    </div>
                    <div className="admin-card-body">
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>SMTP Host</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    placeholder="smtp.gmail.com"
                                    value={smtpSettings.host}
                                    onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>SMTP Port</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    placeholder="587"
                                    value={smtpSettings.port}
                                    onChange={(e) => setSmtpSettings({ ...smtpSettings, port: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>Username</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    placeholder="SMTP username"
                                    value={smtpSettings.username}
                                    onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Password</label>
                                <input
                                    type="password"
                                    className="admin-form-control"
                                    placeholder="SMTP password"
                                    value={smtpSettings.password}
                                    onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>Encryption</label>
                                <select
                                    className="admin-form-control"
                                    value={smtpSettings.encryption}
                                    onChange={(e) => setSmtpSettings({ ...smtpSettings, encryption: e.target.value })}
                                >
                                    <option value="tls">TLS</option>
                                    <option value="ssl">SSL</option>
                                    <option value="none">None</option>
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>From Name</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    value={smtpSettings.fromName}
                                    onChange={(e) => setSmtpSettings({ ...smtpSettings, fromName: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="admin-form-group">
                            <label>From Email</label>
                            <input
                                type="email"
                                className="admin-form-control"
                                placeholder="noreply@caribbargains.com"
                                value={smtpSettings.fromEmail}
                                onChange={(e) => setSmtpSettings({ ...smtpSettings, fromEmail: e.target.value })}
                            />
                        </div>

                        <div className="admin-form-actions" style={{ marginBottom: "25px" }}>
                            <button className="admin-btn admin-btn-primary" onClick={handleSaveSmtp}>
                                <i className="fa fa-save"></i> Save SMTP Settings
                            </button>
                        </div>

                        <div className="admin-settings-section">
                            <h4>Test Email</h4>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <input
                                    type="email"
                                    className="admin-form-control"
                                    placeholder="recipient@example.com"
                                    value={testRecipient}
                                    onChange={(e) => setTestRecipient(e.target.value)}
                                    style={{ maxWidth: "350px" }}
                                />
                                <button className="admin-btn admin-btn-accent" onClick={handleTestSmtp}>
                                    <i className="fa fa-paper-plane"></i> Send Test
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeSection === "security" && (
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3>Security Settings</h3>
                    </div>
                    <div className="admin-card-body">
                        <div className="admin-settings-section">
                            <h4>SSL / HTTPS</h4>
                            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                <label className="admin-toggle">
                                    <input
                                        type="checkbox"
                                        checked={securitySettings.sslRedirect}
                                        onChange={(e) => setSecuritySettings({ ...securitySettings, sslRedirect: e.target.checked })}
                                    />
                                    <span className="admin-toggle-slider"></span>
                                </label>
                                <span style={{ fontSize: "14px", color: securitySettings.sslRedirect ? "var(--admin-primary)" : "#6c757d" }}>
                                    {securitySettings.sslRedirect ? "Force HTTPS redirect is ON" : "HTTPS redirect is OFF"}
                                </span>
                            </div>
                        </div>

                        <div className="admin-settings-section">
                            <h4>Session Security</h4>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Session Timeout (minutes)</label>
                                    <input
                                        type="number"
                                        className="admin-form-control"
                                        value={securitySettings.sessionTimeout}
                                        onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: e.target.value })}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Max Login Attempts</label>
                                    <input
                                        type="number"
                                        className="admin-form-control"
                                        value={securitySettings.maxLoginAttempts}
                                        onChange={(e) => setSecuritySettings({ ...securitySettings, maxLoginAttempts: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="admin-form-actions">
                            <button
                                className="admin-btn admin-btn-primary"
                                onClick={handleSaveSecurity}
                                disabled={saving}
                            >
                                <i className={`fa ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                                {saving ? " Saving..." : " Save Security Settings"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SystemSettings;
